"use client";

import Link from "next/link";
import { ArrowLeft, ArrowRight, CalendarDays, LoaderCircle, Lock, Mail, Phone, ShieldCheck, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { AuthHeading, AuthLayout, AuthNote, StatusPanel } from "@/components/auth-ui";
import { FacilityMatchCard, FieldGroup, FormField, FormSection, PasswordInput, PasswordRules, SelectControl } from "@/components/facility-onboarding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FacilitySummary } from "@/components/facility-summary";
import { getErrorCopy } from "@/lib/error-config";
import { meetsPasswordPolicy, usePasswordStrength } from "@/lib/password-policy";
import { toTitleCase } from "@/lib/utils";
import {
  RegistrationError,
  readSignupIdentity,
  saveSignupIdentity,
  saveSignupResult,
  constantTimeEqual,
  toLegacyRegistryFacility,
  useRegistrationService,
  type SignupIdentity,
  type V3FacilityDetails,
} from "@/lib/registration-service";

/* Steps 2–3 of onboarding_v3: pick facility -> create account -> check email.
   Guest flow: no login required until the verification link is clicked. */

type Stage = "loading" | "choose" | "account" | "link";
const asError = (error: unknown) => error instanceof RegistrationError ? error : new RegistrationError("UNKNOWN", 0);

const GAP_TO_KEY: Record<string, "firstName" | "middleName" | "lastName" | "phoneNumber" | "gender" | "dateOfBirth"> = {
  "First Name": "firstName",
  "Middle Name": "middleName",
  "Last Name": "lastName",
  "Phone Number": "phoneNumber",
  "Gender": "gender",
  "Date of Birth": "dateOfBirth",
};

export default function SignupFacilityPage() {
  const router = useRouter();
  const registration = useRegistrationService();
  const [stage, setStage] = useState<Stage>("loading");
  const [saved, setSaved] = useState<SignupIdentity | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [details, setDetails] = useState<V3FacilityDetails | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profile, setProfile] = useState({ firstName: "", middleName: "", lastName: "", phoneNumber: "", gender: "", dateOfBirth: "" });
  const [fieldError, setFieldError] = useState("");
  const [failure, setFailure] = useState<RegistrationError | null>(null);
  const [busy, setBusy] = useState(false);
  // Frappe weighs the password against the admin's own details too.
  const strength = usePasswordStrength(password, [profile.firstName, profile.middleName, profile.lastName, email, profile.dateOfBirth]);
  // An old message no longer describes what is on screen once a field changes.
  const clearMessages = () => { setFieldError(""); setFailure(null); };

  useEffect(() => {
    const stored = readSignupIdentity();
    // Can't skip ahead: identity (step 1) and phone (step 2) must be done -> otherwise sign-in.
    if (!stored || !stored.phoneVerified) { router.replace("/sign-in"); return; }
    setSaved(stored);
    if (stored) setSelectedId(stored.identity.availableFacilities[0]?.facilityFid ?? "");
    setStage("choose");
  }, [router]);

  const facilities = saved?.identity.availableFacilities ?? [];
  const allFacilities = saved?.identity.facilities ?? [];
  const selected = facilities.find((f) => f.facilityFid === selectedId);
  const alreadyRegistered = saved?.identity.alreadyRegistered === true;

  const continueToAccount = async () => {
    if (!saved || !selectedId) return;
    if (alreadyRegistered) {
      // Existing account: no account form. Remember the pick, then sign in -
      // linking happens automatically after a successful login.
      saveSignupIdentity({ ...saved, selectedFacilityId: selectedId });
      setStage("link");
      return;
    }
    setBusy(true); setFailure(null);
    try {
      const result = await registration.getFacilityDetails(saved.idType, saved.idNumber, selectedId, saved.identity.identityToken);
      setDetails(result);
      // Username doubles as the account email: pre-populate it from the
      // client-registry email. Stays editable - the admin can override it.
      setEmail(result.adminPrefill.email ?? "");
      setProfile({
        firstName: result.adminPrefill.firstName,
        middleName: result.adminPrefill.middleName,
        lastName: result.adminPrefill.lastName,
        phoneNumber: result.adminPrefill.phoneNumber,
        gender: result.adminPrefill.gender,
        dateOfBirth: result.adminPrefill.dateOfBirth,
      });
      setStage("account");
    } catch (error) { setFailure(asError(error)); } finally { setBusy(false); }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFieldError(""); setFailure(null);
    if (!saved) return;
    // The email is the username: reject rather than silently rewrite it.
    if (/\s/.test(email)) { setFieldError("Remove the spaces from your email address."); return; }
    if (email !== email.toLowerCase()) { setFieldError("Enter your email address in lowercase letters."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setFieldError("Enter a valid email address. The verification link will be sent there."); return; }
    if (!meetsPasswordPolicy(password)) { setFieldError("Your password does not meet every requirement listed under the field."); return; }
    if (strength === "weak") { setFailure(new RegistrationError("PASSWORD_TOO_WEAK", 400)); return; }
    if (!constantTimeEqual(password, confirmPassword)) { setFieldError("Passwords do not match."); return; }
    const requiredGaps = (details?.missingAdminFields ?? []).map((label) => GAP_TO_KEY[label]).filter(Boolean).filter((key) => key !== "middleName");
    for (const key of requiredGaps) {
      if (!profile[key].trim()) { setFieldError(`Enter the ${key.replace(/([A-Z])/, " $1").toLowerCase()} from your official record.`); return; }
    }
    setBusy(true);
    try {
      const gapPayload: Record<string, string> = {};
      const keyToField: Record<string, string> = { firstName: "first_name", middleName: "middle_name", lastName: "last_name", phoneNumber: "phone_number", gender: "gender", dateOfBirth: "date_of_birth" };
      for (const [key, field] of Object.entries(keyToField)) {
        const value = profile[key as keyof typeof profile].trim();
        if (value) gapPayload[field] = value;
      }
      const result = await registration.onboardFacility({
        // Sign-in is by email: the email doubles as the username (Revision 2d).
        username: email.trim(),
        email: email.trim(),
        password,
        confirmPassword,
        identificationType: saved.idType,
        identificationNumber: saved.idNumber,
        facilityId: selectedId,
        identityToken: saved.identity.identityToken,
        profile: gapPayload,
      });
      saveSignupResult({ ...result, adminEmail: email.trim() });
      router.push("/sign-up/complete");
    } catch (error) {
      setFailure(asError(error)); setBusy(false);
    }
  };

  // Password rejections carry Frappe's raw zxcvbn wording: show our copy.
  const failurePanel = failure && <StatusPanel tone="danger" title={getErrorCopy(failure.code).title}>{failure.code === "PASSWORD_TOO_WEAK" ? getErrorCopy(failure.code).message : failure.detail ?? getErrorCopy(failure.code).message}</StatusPanel>;
  const gapLabels = details?.missingAdminFields ?? [];

  if (stage === "account" && selected && details) {
    const setField = (key: keyof typeof profile) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => { clearMessages(); setProfile({ ...profile, [key]: event.target.value }); };
    // Names capitalise as typed (F5) so the posted value matches what is shown.
    const setName = (key: "firstName" | "middleName" | "lastName") => (event: React.ChangeEvent<HTMLInputElement>) => { clearMessages(); setProfile({ ...profile, [key]: toTitleCase(event.target.value) }); };
    return <AuthLayout variant="sign-up" step={4} size="full">
      <button type="button" className="auth-back link-button" onClick={() => { setFailure(null); setStage("choose"); }}><ArrowLeft size={16} />Back</button>
      <AuthHeading overline="Step 4 of 7" title="Create Your Account">This creates the administrator account and onboards the facility. You’ll verify your email to activate it.</AuthHeading>
      <FacilitySummary facility={details.facility} />
      <form className="auth-stack" onSubmit={submit} noValidate>
        <FormSection id="account-title" icon={UserRound} title="Administrator Account" description="You’ll sign in with these details once your email is verified.">
          <div className="form-card-body">
            {details.adminPrefill.maskedEmail && <AuthNote icon={ShieldCheck}>Registry email on file: <strong>{details.adminPrefill.maskedEmail}</strong>. The verification link goes to the email you enter below.</AuthNote>}
            <FieldGroup title="Sign in with">
              <FormField id="email" label="Email" required icon={Mail} hint="You’ll sign in with this email. The verification link is sent here."><Input id="email" type="email" value={email} onChange={(e) => { clearMessages(); setEmail(e.target.value); }} autoComplete="email" placeholder="you@example.com" /></FormField>
              <div className="form-row cols-2">
                <div className="password-field">
                  <FormField id="password" label="Password" required icon={Lock}><PasswordInput id="password" value={password} onChange={(e) => { clearMessages(); setPassword(e.target.value); }} autoComplete="new-password" aria-describedby="password-rules" /></FormField>
                  <PasswordRules id="password-rules" value={password} strength={strength} />
                </div>
                <FormField id="confirm" label="Confirm password" required icon={Lock}><PasswordInput id="confirm" value={confirmPassword} onChange={(e) => { clearMessages(); setConfirmPassword(e.target.value); }} autoComplete="new-password" /></FormField>
              </div>
            </FieldGroup>
            {gapLabels.length > 0 && <FieldGroup title="Administrator Details">
              <p className="auth-hint">The registry did not hold these. They are required to continue.</p>
              <div className="form-row cols-2">
                {gapLabels.includes("First Name") && <FormField id="gap-first" label="First name" required icon={UserRound}><Input id="gap-first" value={profile.firstName} onChange={setName("firstName")} autoComplete="given-name" autoCapitalize="words" /></FormField>}
                {gapLabels.includes("Middle Name") && <FormField id="gap-middle" label="Middle name" icon={UserRound}><Input id="gap-middle" value={profile.middleName} onChange={setName("middleName")} autoComplete="additional-name" autoCapitalize="words" /></FormField>}
                {gapLabels.includes("Last Name") && <FormField id="gap-last" label="Last name" required icon={UserRound}><Input id="gap-last" value={profile.lastName} onChange={setName("lastName")} autoComplete="family-name" autoCapitalize="words" /></FormField>}
                {gapLabels.includes("Phone Number") && <FormField id="gap-phone" label="Phone number" required icon={Phone}><Input id="gap-phone" type="tel" inputMode="tel" value={profile.phoneNumber} onChange={setField("phoneNumber")} placeholder="0712 345 678" /></FormField>}
                {gapLabels.includes("Gender") && <FormField id="gap-gender" label="Gender" required><SelectControl><select id="gap-gender" className="input select" value={profile.gender} onChange={setField("gender")}>
                  <option value="">Choose gender</option>
                  {Array.from(new Set(["Female", "Male", "Other", profile.gender].filter(Boolean))).map((option) => <option key={option} value={option}>{option}</option>)}
                </select></SelectControl></FormField>}
                {gapLabels.includes("Date of Birth") && <FormField id="gap-dob" label="Date of birth" required icon={CalendarDays}><Input id="gap-dob" type="date" value={profile.dateOfBirth} onChange={setField("dateOfBirth")} /></FormField>}
              </div>
            </FieldGroup>}
          </div>
        </FormSection>
        {fieldError && <span className="field-error" role="alert">{fieldError}</span>}
        {failurePanel}
        <div className="form-footer">
          <span>Fields marked * are required</span>
          <Button type="button" variant="secondary" className="u-w-auto" onClick={() => { setFailure(null); setStage("choose"); }}><ArrowLeft size={16} />Back</Button>
          <Button type="submit" className="u-w-auto" disabled={busy}>{busy ? <><LoaderCircle size={16} className="spin" />Onboarding…</> : <>Onboard facility <ArrowRight size={16} /></>}</Button>
        </div>
      </form>
    </AuthLayout>;
  }

  if (stage === "link" && selected && saved) {
    // Already-registered IDs sign in to link - then resume the stepper
    // (Complete setup) for THIS facility instead of landing on the
    // dashboard. The facility travels in the URL so sign-in can't fall
    // back to the first facility on the account.
    const signInHref = `/sign-in?next=${encodeURIComponent(`/sign-up/setup?facility_id=${encodeURIComponent(selectedId)}`)}`;
    return <AuthLayout variant="sign-up" step={3} size="narrow">
      <button type="button" className="auth-back link-button" onClick={() => { setFailure(null); setStage("choose"); }}><ArrowLeft size={16} />Back</button>
      <AuthHeading overline="Step 3 of 7" title="Already Registered">This ID already has an account{saved.identity.maskedEmail ? <> ({saved.identity.maskedEmail})</> : ""} - no new password needed.</AuthHeading>
      <StatusPanel tone="info" title={`Link ${selected.name}`}>Sign in with your existing account and we’ll link this facility to it automatically.</StatusPanel>
      <div className="auth-stack">
        <Button asChild><Link href={signInHref}>Sign in to link facility <ArrowRight size={16} /></Link></Button>
      </div>
    </AuthLayout>;
  }

  return <AuthLayout variant="sign-up" step={3} size="wide">
    <AuthHeading overline="Step 3 of 7" title={allFacilities.length > 1 ? "Which Facility Are You Registering?" : "Is This Your Facility?"}>The facility registry lists you as its administrator. Each facility is onboarded once.</AuthHeading>
    {stage === "loading" && <div className="loading-block" aria-label="Finding your facilities"><div className="skeleton" /><div className="skeleton" /></div>}
    {stage === "choose" && saved && facilities.length > 0 && <div className={facilities.length > 1 ? "facility-choices" : undefined} role={facilities.length > 1 ? "radiogroup" : undefined} aria-label="Your registry facilities">
      {allFacilities.map((facility) => <FacilityMatchCard key={facility.facilityFid} facility={toLegacyRegistryFacility({ ...facility, isOnboarded: facility.isOnboarded })} selectable={facilities.length > 1} selected={facility.facilityFid === selectedId} onSelect={() => setSelectedId(facility.facilityFid)} />)}
    </div>}
    {stage === "choose" && saved && facilities.length === 0 && <StatusPanel tone="info" title="All your facilities are registered">Every facility under this ID is already onboarded. <Link className="app-link" href="/sign-in">Sign in</Link> with the administrator account.</StatusPanel>}
    {failurePanel}
    {stage === "choose" && saved && facilities.length > 0 && <>
      <AuthNote icon={ShieldCheck}>{alreadyRegistered
        ? "Pick the facility to link - you’ll sign in with your existing account, no new password needed."
        : "Next you’ll set the email and password you’ll sign in with."}</AuthNote>
      <div className="auth-actions">
        <Button type="button" onClick={continueToAccount} disabled={busy || !selected || selected.isOnboarded}>{busy ? <><LoaderCircle size={16} className="spin" />Loading…</> : <>Yes, continue <ArrowRight size={16} /></>}</Button>
      </div>
    </>}
    <p className="auth-switch"><span>Not your facility?</span><Link href="/contact">Contact support</Link></p>
  </AuthLayout>;
}
